import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  WebsiteBandwidthUsage,
  WebsiteGroupBandwidthUsage,
} from '../../../../shared/models/usage.models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { SearchFieldComponent } from '../../../../shared/components/search-field/search-field.component';
import { BytesToSizePipe } from '../../../../shared/pipes/bytes-to-size-pipe';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatRippleModule } from '@angular/material/core';
import { Plan } from '../../../../shared/models/plans.models';

@Component({
  selector: 'app-bandwidth',
  imports: [
    MatCardModule,
    MatProgressBarModule,
    FontAwesomeModule,
    TableComponent,
    SearchFieldComponent,
    MatTableModule,
    MatRippleModule,
    BytesToSizePipe,
  ],
  providers: [BytesToSizePipe],
  templateUrl: './bandwidth.component.html',
  styleUrl: './bandwidth.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BandwidthComponent {
  private sizePipe = inject(BytesToSizePipe);

  bandwidth = input.required<WebsiteGroupBandwidthUsage>();

  plan = input<Plan>();

  filters = signal<{ page: number; itemsPerPage: number; domain?: string }>({
    page: 1,
    itemsPerPage: 10,
  });

  bandwidthPercent = computed(() => {
    const usage = this.bandwidth().totalUsage;
    const limit = this.plan()?.bandwidth || 0 / 1024;
    if (usage && limit) return (usage / limit) * 100;
    return 0;
  });

  bandwidthLimit = computed(() => this.plan()?.bandwidth || 0 * 1024);

  websitesData = computed(() => {
    const records = this.bandwidth().websiteUsage ?? [];

    // Search
    const filteredRecords = this.filters().domain
      ? records.filter((record) =>
          record.websiteDomain.toLowerCase().includes(this.filters().domain!.toLowerCase()),
        )
      : records;

    // Pagination
    const start = (this.filters().page - 1) * this.filters().itemsPerPage;
    const end = start + this.filters().itemsPerPage;

    return filteredRecords.slice(start, end);
  });

  websitesColumns = [
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: WebsiteBandwidthUsage) => `${element.websiteDomain}`,
    },
    {
      columnDef: 'traffic',
      header: 'Site traffic',
      cell: (element: WebsiteBandwidthUsage) =>
        `${this.sizePipe.transform(element.usage.reduce((acc, curr) => acc + curr.usage, 0)) || '-'}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1 }));
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, domain: text }));
  }
}
